import { Injectable, OnDestroy } from "@angular/core";
import { WebservicesService } from "src/app/services/webservices/webservices.service";
import { ConfigService } from "src/app/services/config/config.service";
import { Subject, combineLatest, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UserSessionService } from "../user-session/user-session.service";
import { LobbyApiService } from "./lobby.api.service";
import { EventsService } from "../event/event.service";

@Injectable({
  providedIn: "root",
})
export class GamesService implements OnDestroy {
  private userStatus: any;
  public favouriteGames: any[];
  public recentGames: any[];
  public allGames: any[];
  public primaryGames: any[];
  public lobbyName: any;
  public favouriteGameStore: any;
  private userLobbySubcriber: any;
  private flattened_array:any[];

  _games: Subject<any> = new Subject();
  _lobbyName: Subject<any> = new Subject();
  _destroy: Subject<any> = new Subject();

  constructor(
    private webService: WebservicesService,
    private config: ConfigService,
    private userService: UserSessionService,
    private lobbyApiService: LobbyApiService,
    private events: EventsService
  ){
    this.userLobbySubcriber = combineLatest([
      this.userService.getUserStatus(),
      this._lobbyName,
    ]).pipe(takeUntil(this._destroy)).subscribe(([userStatus, lobbyName]) => {
      if (userStatus && lobbyName) {
        this.updateUserStatus(userStatus);
        this.getAllGames();
      } else {
        if (!userStatus) {
          this.resetOnLogout();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userLobbySubcriber) this.userLobbySubcriber.unsubscribe();
    this._destroy.next();
    this._destroy.complete();
  }

  isFavourite(game_id) {
    if (!this.favouriteGames) return false;
    return game_id in this.favouriteGameStore;
  }

  getGames() {
    return this._games;
  }

  setGames(allGames) {
    this._games.next(allGames);
    this.events.publish('event:checkRecent:Fav:games')
  }

  async getAllGames() {
    if (this.userStatus == null) return;
    let result = await Promise.resolve(this.fetchGames());
    this.primaryGames = result.payload;
    result = await Promise.resolve(this.getFavourites());
    this.createFavouriteStore(result.payload);
    this.favouriteGames = result.payload;
    await this.getRecentGames();
    if (!this.primaryGames["Favorites"]) this.combineAndEmit(this.primaryGames);
  }

  updateUserStatus(userStatus) {
    this.userStatus = userStatus;
  }

  setLobbyName(lobbyName) {
    this.lobbyName = lobbyName;
    this._lobbyName.next(this.lobbyName);
  }

  combineAndEmit(games, favorite = this.favouriteGames) {
    if (this.recentGames) games["Recent Games"] = this.recentGames;
    games["Favorites"] = favorite;
    this.allGames = games;
    this.setGames(this.allGames);
    this.flattened_array =  Object.values(this.allGames).reduce((acc, curr) => [...acc, ...curr], []);
  }
  
  async getRecentGames() {
    let result = await Promise.resolve(this.getRecent());
    this.recentGames = result.payload;
    this.events.publish('event:checkRecent:Fav:games')
  }

  createFavouriteStore(payload) {
    this.favouriteGameStore = {};
    payload.forEach((game) => {
      this.favouriteGameStore[game.id] = "1";
    });
  }

  getFavourites() {
    return this.lobbyApiService.getFavourites().toPromise();
  }

  getRecent() {
    return this.lobbyApiService.getRecent(this.lobbyName).toPromise();
  }

  addFavourites(game_id) {
    return this.lobbyApiService.addFavourites(game_id).toPromise();
  }

  fetchGames() {
    return this.lobbyApiService.fetchGamesByLobbyName(this.lobbyName)
      .toPromise();
  }

  async removeFavouritesAndRecreate(game_id) {
    const response = await this.removeFavourites(game_id);
    if (response.result) {
      this.favouriteGames = response.payload;
      this.createFavouriteStore(response.payload);
      this.combineAndEmit(this.primaryGames);
    } else {
      console.log("Error removing Fav Games", response);
    }
  }

  async addFavouritesAndRecreate(gamedata) {
    const response = await this.addFavourites(gamedata.id);
    if (response.result) {
      this.favouriteGames = response.payload;
      this.createFavouriteStore(response.payload);
      this.combineAndEmit(this.primaryGames);
    } else {
      console.log("Error adding Fav Games", response);
    }
  }

  removeRecentAndRecreate(game_id) {
    this.removeRecent(game_id);
    this.recentGames = this.recentGames.filter((game) => game.id != game_id);
  }

  removeFavourites(game_id) {
    return this.lobbyApiService.removeFavourites(game_id).toPromise();
  }

  removeRecent(game_id) {
    return this.lobbyApiService.removeRecent(game_id).toPromise();
  }

  resetOnLogout() {
    this.updateUserStatus(null);
    if (this.primaryGames && this.primaryGames["Favorites"]) {
      this.combineAndEmit(this.primaryGames, []);
    }
  }
  
  getGameById(id: number) { 
    return this.flattened_array.find((game) => game.id == id);
  }

  filterGamesWithListBasedOnIds(gameFilterArray: any[]) {
    let tempArray = [...gameFilterArray];
    if ( tempArray) {
        let gameArray = [];

        for(let gameInner of tempArray) {
          let value = this.flattened_array.find(value => gameInner.id == value.id);
          if (value) gameArray.push(value);
        }
        
        return of(gameArray);
    } else {
      return of([]);
    };
  }
}
